import React, { useMemo } from 'react'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SearchIcon from '@mui/icons-material/Search'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import GuidanceNote from '../../components/GuidanceNote'
import {
  dialogFormCardSx,
  dialogSearchFieldSx,
  dialogSearchIconSx,
  guidanceNoteIconSx,
  guidanceNoteWarningColors,
  guidanceNoteWarningAccentMessageSx,
  guidanceNoteWarningHeaderSx,
  guidanceNoteWarningSx
} from '../../constants/dialogSx'
import RbacDialogFieldLabel from './shared/RbacDialogFieldLabel'
import {
  createUserInfoNoteColors,
  createUserInfoNoteMessageSx,
  createUserInfoNoteSx,
  createUserInfoNoteTitleSx
} from './constants/rbacCreateUserDialogConstants'

export default function RbacUserBasicDataForm({
  mode,
  hidden,
  canEditEmail,
  roles,
  form,
  errors,
  onFieldChange
}) {
  const isEdit = mode === 'edit'
  const selectedRole = useMemo(
    () => roles.find((role) => form.roleIds.includes(role.id)) || null,
    [form.roleIds, roles]
  )

  return (
    <Box hidden={hidden}>
      {isEdit && canEditEmail && (
        <GuidanceNote
          icon={<WarningAmberOutlinedIcon />}
          title=""
          message="Este usuario todavía no ha activado su cuenta. Si cambias su correo, se enviará la invitación a la nueva dirección."
          accentColor={guidanceNoteWarningColors.accentColor}
          backgroundColor={guidanceNoteWarningColors.backgroundColor}
          textColor={guidanceNoteWarningColors.textColor}
          sx={guidanceNoteWarningSx}
          headerSx={guidanceNoteWarningHeaderSx}
          iconSx={guidanceNoteIconSx}
          messageSx={guidanceNoteWarningAccentMessageSx}
          singleLine
        />
      )}

      {!isEdit && (
        <GuidanceNote
          icon={<InfoOutlinedIcon />}
          title={null}
          message={
            <>
              Al crear el usuario, se le enviará un correo para que active su cuenta y defina su
              contraseña. Mientras tanto, estará en estado <strong>pendiente.</strong>
            </>
          }
          accentColor={createUserInfoNoteColors.accentColor}
          backgroundColor={createUserInfoNoteColors.backgroundColor}
          textColor={createUserInfoNoteColors.textColor}
          singleLine
          sx={createUserInfoNoteSx}
          headerSx={guidanceNoteWarningHeaderSx}
          iconSx={guidanceNoteIconSx}
          titleSx={createUserInfoNoteTitleSx}
          messageSx={createUserInfoNoteMessageSx}
        />
      )}

      <Box sx={dialogFormCardSx}>
        <Box className="rbac-user-dialog__grid">
          <Box className="rbac-user-dialog__full">
            <RbacDialogFieldLabel htmlFor={`${mode}-rbac-user-name`} required>
              Nombre completo
            </RbacDialogFieldLabel>
            <TextField
              id={`${mode}-rbac-user-name`}
              placeholder="Nombre completo"
              size="small"
              autoFocus
              value={form.name}
              onChange={(event) => onFieldChange('name', event.target.value)}
              error={Boolean(errors.name)}
              helperText={errors.name}
              required
              fullWidth
              hiddenLabel
              InputLabelProps={{ shrink: false }}
              sx={dialogSearchFieldSx}
            />
          </Box>
          <Box className="rbac-user-dialog__full">
            <RbacDialogFieldLabel htmlFor={`${mode}-rbac-user-role`} required>
              Rol de usuario
            </RbacDialogFieldLabel>
            <Autocomplete
              options={roles}
              value={selectedRole}
              size="small"
              fullWidth
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, nextRole) =>
                onFieldChange('roleIds', nextRole ? [nextRole.id] : [])
              }
              noOptionsText="No hay roles disponibles"
              renderInput={(params) => (
                <TextField
                  {...params}
                  id={`${mode}-rbac-user-role`}
                  placeholder="Buscar o asignar un rol"
                  size="small"
                  required
                  error={Boolean(errors.roleIds)}
                  helperText={errors.roleIds}
                  hiddenLabel
                  InputLabelProps={{ ...params.InputLabelProps, shrink: false }}
                  sx={dialogSearchFieldSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start" sx={{ mr: 0.5 }}>
                          <SearchIcon sx={dialogSearchIconSx} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Box>
          <Box>
            <RbacDialogFieldLabel htmlFor={`${mode}-rbac-user-email`} required>
              Correo electrónico
            </RbacDialogFieldLabel>
            <TextField
              id={`${mode}-rbac-user-email`}
              placeholder="ejemplo@email.com"
              type="email"
              size="small"
              value={form.email}
              onChange={(event) => onFieldChange('email', event.target.value)}
              error={Boolean(errors.email)}
              helperText={errors.email}
              required
              fullWidth
              disabled={!canEditEmail}
              hiddenLabel
              InputLabelProps={{ shrink: false }}
              sx={dialogSearchFieldSx}
            />
          </Box>
          <Box>
            <RbacDialogFieldLabel htmlFor={`${mode}-rbac-user-phone`}>
              Número de teléfono (opcional)
            </RbacDialogFieldLabel>
            <TextField
              id={`${mode}-rbac-user-phone`}
              placeholder="0000-0000"
              size="small"
              value={form.phone}
              onChange={(event) => onFieldChange('phone', event.target.value)}
              fullWidth
              hiddenLabel
              InputLabelProps={{ shrink: false }}
              inputProps={{ maxLength: 18, inputMode: 'tel' }}
              sx={dialogSearchFieldSx}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
